import React, { useEffect, useRef, useCallback, useState } from 'react'
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameWeek, addMonths, subMonths } from 'date-fns'

export interface DatePickerOption {
  value: string
  label: string
  date?: Date
}

interface DatePickerDropdownProps {
  id: string
  label?: string
  selectedDate: Date
  onDateChange: (date: Date) => void
  calendarView?: 'day' | 'week'
  onViewChange?: (view: 'day' | 'week') => void
  minWidth?: string
  disabled?: boolean
  transparency?: 'low' | 'medium' | 'high' | 'none'
  includeTime?: boolean
  selectedTime?: string // Format: "HH:MM"
  onTimeChange?: (time: string) => void
  timeLabel?: string
  autoShowCalendar?: boolean // New prop to control calendar display
  weekStartsOn?: 0 | 1 // 0 = Sunday, 1 = Monday (default)
}

export default function DatePickerDropdown({
  id,
  label,
  selectedDate,
  onDateChange,
  calendarView,
  onViewChange,
  minWidth = "min-w-[280px]",
  disabled = false,
  transparency = 'medium',
  includeTime = false,
  selectedTime = '',
  onTimeChange,
  timeLabel = 'Time',
  autoShowCalendar = false,
  weekStartsOn = 1
}: DatePickerDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(selectedDate)

  const handleToggle = () => {
    if (disabled) return
    if (!isOpen) {
      // When opening dropdown, set currentMonth to show the selected date's month
      setCurrentMonth(selectedDate)
    }
    setIsOpen(!isOpen)
  }

  const handleDateClick = (date: Date) => {
    // If time is included, preserve the current time
    if (includeTime && selectedTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number)
      date.setHours(hours, minutes, 0, 0)
    }
    onDateChange(date)
    setIsOpen(false)
  }

  const handleWeekClick = (weekStart: Date) => {
    // If time is included, preserve the current time
    if (includeTime && selectedTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number)
      weekStart.setHours(hours, minutes, 0, 0)
    }
    onDateChange(weekStart)
    setIsOpen(false)
  }

  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1))
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onTimeChange) {
      onTimeChange(e.target.value)
    }
  }

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])

  const getSelectedDisplay = () => {
    let display = ''
    
    if (calendarView === 'day') {
      display = format(selectedDate, 'MMM d, yyyy')
    } else {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn })
      const weekEnd = endOfWeek(selectedDate, { weekStartsOn })
      display = `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
    }

    if (includeTime && selectedTime) {
      display += ` ${selectedTime}`
    }

    return (
      <span className="text-sm text-gray-900 truncate block w-full pr-6">
        {display}
      </span>
    )
  }

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn })

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd })
    const weeks = []
    
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7))
    }

    // Check if we can navigate to next month (disable if next month is in the future)
    const today = new Date()
    const nextMonth = addMonths(currentMonth, 1)
    const canNavigateNext = startOfMonth(nextMonth) <= startOfMonth(today)

    return (
      <div className="p-4">
        {/* Header with month navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => handleMonthChange('prev')}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-sm font-medium text-gray-900">
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          <button
            onClick={() => handleMonthChange('next')}
            disabled={!canNavigateNext}
            className={`p-1 rounded ${canNavigateNext ? 'hover:bg-gray-100 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {(weekStartsOn === 0
            ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
            : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
          ).map(day => (
            <div key={day} className="text-xs text-gray-500 text-center py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {weeks.map((week, weekIndex) => (
            <React.Fragment key={weekIndex}>
              {week.map((day) => {
                const isCurrentMonth = day.getMonth() === currentMonth.getMonth()
                const isToday = isSameDay(day, new Date())
                const isFutureDate = day > today
                const isSelected = calendarView === 'day'
                  ? isSameDay(day, selectedDate)
                  : isSameWeek(day, selectedDate, { weekStartsOn })

                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => {
                      if (isFutureDate) return; // Prevent clicking future dates
                      if (calendarView === 'day') {
                        handleDateClick(day);
                      } else {
                        handleWeekClick(startOfWeek(day, { weekStartsOn }));
                      }
                    }}
                    disabled={isFutureDate}
                    className={`
                      w-8 h-8 text-xs rounded flex items-center justify-center transition-colors
                      ${isFutureDate 
                        ? 'text-gray-300 cursor-not-allowed bg-gray-50' 
                        : isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                      }
                      ${isToday && !isFutureDate ? 'bg-blue-100 text-blue-900 font-semibold' : ''}
                      ${isSelected && !isFutureDate ? 'bg-blue-600 text-white font-semibold' : ''}
                      ${!isSelected && !isToday && !isFutureDate ? 'hover:bg-gray-100' : ''}
                    `}
                  >
                    {format(day, 'd')}
                  </button>
                )
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Time input */}
        {includeTime && (
          <div className="mt-4 pt-3 border-t border-gray-200">
            <label htmlFor={`${id}-time`} className="block text-xs text-gray-500 mb-1">
              {timeLabel}
            </label>
            <input
              type="time"
              id={`${id}-time`}
              value={selectedTime}
              onChange={handleTimeChange}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}

        {/* View toggle - only show if calendarView and onViewChange are provided */}
        {calendarView && onViewChange && (
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">View:</span>
              <div className="flex items-center space-x-1 bg-gray-100 rounded p-1">
                <button
                  onClick={() => onViewChange('day')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    calendarView === 'day'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Day
                </button>
                <button
                  onClick={() => onViewChange('week')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    calendarView === 'week'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Week
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // If autoShowCalendar is true, show calendar directly
  if (autoShowCalendar) {
    return (
      <div id={`${id}-filter`}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative" ref={dropdownRef}>
          <div 
            className={`${minWidth} border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${
              disabled ? 'bg-gray-100' : 'bg-white'
            }`}
          >
            <div 
              className={`${transparency === 'none' 
                ? 'bg-white border border-gray-200' 
                : transparency === 'low'
                ? 'bg-gradient-to-b from-white/80 to-white/70 backdrop-blur-sm border border-gray-200/60'
                : transparency === 'high'
                ? 'bg-gradient-to-b from-white/20 to-white/10 backdrop-blur-lg border border-gray-200/20'
                : 'bg-gradient-to-b from-white/40 to-white/30 backdrop-blur-md border border-gray-200/30'
              } rounded-md shadow-lg`}
            >
              {renderCalendar()}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Original dropdown behavior
  return (
    <div id={`${id}-filter`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <div 
          className={`min-h-[42px] ${minWidth} border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 p-2 relative ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'
          }`}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleToggle()
            }
          }}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {getSelectedDisplay()}
          
          {/* Dropdown arrow button */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <button
              type="button"
              onClick={handleToggle}
              disabled={disabled}
              data-testid="dropdown-toggle"
              className={`text-gray-400 hover:text-gray-600 focus:outline-none ${
                disabled ? 'cursor-not-allowed' : ''
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Calendar Dropdown */}
        <div 
          id={id}
          className={`${isOpen ? '' : 'hidden'} absolute z-10 mt-1 left-0 ${
            transparency === 'none' 
              ? 'bg-white border border-gray-200' 
              : transparency === 'low'
              ? 'bg-gradient-to-b from-white/80 to-white/70 backdrop-blur-sm border border-gray-200/60'
              : transparency === 'high'
              ? 'bg-gradient-to-b from-white/20 to-white/10 backdrop-blur-lg border border-gray-200/20'
              : 'bg-gradient-to-b from-white/40 to-white/30 backdrop-blur-md border border-gray-200/30'
          } rounded-md shadow-xl`}
        >
          {renderCalendar()}
        </div>
      </div>
    </div>
  )
} 